import { useCallback, useState, FC, MouseEvent } from "react";
import { EmotionClassStyles } from "@/domains/emotion/types";
import {
  MdsDropdownContentList,
  MdsDropdownProps,
  MdsDropdown,
} from "@/design-system/components/dropdown";
import { MdsIconKind } from "@/design-system/components/icon";
import classNames from "classnames";
import { IMDSIconButtonProps, MDSIconButton, MDSTooltip, MDSTooltipConfig } from "@/mds";
import { css, cx } from "@/domains/emotion";

export interface MdsDropdownIconButtonProps extends EmotionClassStyles {
  buttonClassName?: string;
  contentList: MdsDropdownContentList;
  iconKind?: MdsIconKind;
  size?: IMDSIconButtonProps["size"];
  variant?: IMDSIconButtonProps["variant"];
  isDisabled?: IMDSIconButtonProps["isDisabled"];
  isLoading?: IMDSIconButtonProps["isLoading"];
  placement?: MdsDropdownProps["placement"];
  tooltipConfig?: MDSTooltipConfig;
  innerStyles?: MdsDropdownProps["innerStyles"];
  onOpenChange?: (isOpen: boolean) => void;
}

/**
 * @deprecated - Use the newer MDS components instead.
 *
 * - If something doesn't exist yet, please create a new MDS component and
 *   have it reviewed by the team!
 *
 * - If something needs to hook into our `stores`, add it to the /components directory.
 *
 * - If something is a one-off; please colocate it with the component/page that
 *   needs it.
 *
 * - If you have questions, please ask in the #hub-eng-web-team Slack channel.
 */
export const MdsDropdownIconButton: FC<MdsDropdownIconButtonProps> = ({
  className,
  buttonClassName,
  contentList,
  iconKind = MdsIconKind.More,
  variant,
  size,
  isDisabled,
  isLoading,
  placement,
  tooltipConfig,
  innerStyles,
  onOpenChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const handleOpenChange = useCallback(
    (newIsOpen: boolean) => {
      setIsOpen(newIsOpen);
      onOpenChange?.(newIsOpen);
    },
    [onOpenChange]
  );

  const handleClick = useCallback(
    (event: MouseEvent) => {
      event.stopPropagation();
      const newIsOpen = !isOpen;
      setIsOpen(newIsOpen);
      onOpenChange?.(newIsOpen);
    },
    [isOpen, onOpenChange]
  );

  const dropdownStyles = css({ display: "flex", alignItems: "center", justifyContent: "center" });

  return (
    <MdsDropdown
      className={cx(className, dropdownStyles)}
      isOpen={isOpen}
      onOpenChange={handleOpenChange}
      contentList={contentList}
      placement={placement}
      innerStyles={innerStyles}
    >
      <MDSTooltip config={tooltipConfig}>
        <MDSIconButton
          variant={variant}
          size={size}
          className={classNames(buttonClassName, "mds-dropdown-icon-button")}
          iconKind={iconKind}
          isDisabled={isDisabled}
          isLoading={isLoading}
          onClick={handleClick}
        />
      </MDSTooltip>
    </MdsDropdown>
  );
};
